import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { getApiUrl } from '../core/helpers/rest-utils';
import { Observable } from 'rxjs';
import {
  App,
  AppCategoryGroup,
  AppCategoryOption,
  AppFieldStage,
  AppFieldType,
  AppFormField,
  AppFormFieldOption,
  AppFormFieldValidation,
  AppKey,
  AppProfile,
  AppSetup,
  AppStatus,
  AppTranslationConfig,
  CrmFilters,
  DefaultApp,
  RawApp,
  RawFormField
} from './app/app';
import { map } from 'rxjs/operators';
import { FieldBehaviour } from '../shared/display-field/display-field.component';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { setRequired } from '../core/helpers/form-utils';
import { Router } from '@angular/router';
import { CaseType, convertCamelCaseToSnakeCase, sortArrayByNumericAttribute } from '../core/helpers/utils';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../core/user/user.service';
import { AnalyticsEvent, InternalAnalyticsService } from '../core/internal-analytics.service';
import { DataStorage } from '../core/data/data-storage';
import { SessionService } from '../core/session.service';

export interface AppsFilterState {
  searchTerm: string;
  selectedCategories: string[];
  connectedOnly: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AppsService {
  public allApps: App[] = [];
  /** Ordered category options from the API meta — drives group order and the Type filter. */
  public categories: AppCategoryOption[] = [];

  // Search + filters for the integrations list (ENG-2249). The catalog is fully
  // loaded, so filtering is client-side; state persists for the browser session
  // like the conversation inbox filters.
  searchTerm = '';
  selectedCategories: string[] = [];
  connectedOnly = false;
  private filterStorage: DataStorage = DataStorage.session();
  private readonly FILTER_STORAGE_KEY = 'integrations-filters';
  private loadedForBusinessId: string = null;
  private readonly session = inject(SessionService);

  constructor(private http: HttpClient, private formBuilder: UntypedFormBuilder, private router: Router, private user: UserService, private translate: TranslateService, private analytics: InternalAnalyticsService) {
    this.restoreFilters();
    // This service is a root singleton, so the catalog and the session filters
    // would otherwise outlive the business they were loaded for (multi-account
    // switch) or the user who set them (logout and login in the same tab).
    this.session.terminate.subscribe(() => this.forgetBusinessState());
    this.session.refreshState.subscribe(() => this.forgetBusinessStateIfSwitched());
  }

  get apps(): App[] {
    return this.allApps;
  }

  /** Whether the loaded catalog belongs to the active business. */
  get isCatalogCurrent(): boolean {
    return this.allApps.length > 0 && this.loadedForBusinessId === this.user.activeBusiness?.id;
  }

  /** Apps that belong on the list at all (regardless of search/filters). */
  get listedApps(): App[] {
    return this.allApps
      .filter((app) => !app.isHiddenFromList)
      .sort((a, b) => Number(b.isPreferredPartner) - Number(a.isPreferredPartner) || a.name.localeCompare(b.name));
  }

  get hasSearchTerm(): boolean {
    return this.searchTerm.trim().length > 0;
  }

  get hasCategoryFilter(): boolean {
    return this.selectedCategories.length > 0;
  }

  get hasAdvancedFilters(): boolean {
    return this.hasCategoryFilter || this.connectedOnly;
  }

  get advancedFilterCount(): number {
    return this.selectedCategories.length + (this.connectedOnly ? 1 : 0);
  }

  get hasAnyActiveFilters(): boolean {
    return this.hasAdvancedFilters || this.hasSearchTerm;
  }

  isCategorySelected(key: string): boolean {
    return this.selectedCategories.includes(key);
  }

  toggleCategory(key: string): void {
    this.selectedCategories = this.isCategorySelected(key)
      ? this.selectedCategories.filter((selected) => selected !== key)
      : [...this.selectedCategories, key];
    this.persistFilters();
  }

  setConnectedOnly(connectedOnly: boolean): void {
    this.connectedOnly = connectedOnly;
    this.persistFilters();
  }

  setSearchTerm(term: string): void {
    this.searchTerm = term ?? '';
    this.persistFilters();
  }

  clearAdvancedFilters(): void {
    this.selectedCategories = [];
    this.connectedOnly = false;
    this.persistFilters();
  }

  clearAllFilters(): void {
    this.searchTerm = '';
    this.clearAdvancedFilters();
  }

  getCategoryLabel(key: string): string {
    const option = this.categories.find((category) => category.key === key);
    return option ? option.label : this.humanizeCategoryKey(key);
  }

  // Getters run on every change-detection tick; returning fresh objects each
  // time makes *ngFor rebuild the pills between mousedown and mouseup, so
  // clicks never land. Memoise on the catalog the options derive from.
  private availableCategoriesCache: { apps: App[]; categories: AppCategoryOption[]; options: AppCategoryOption[] } = null;

  /** Category options that actually have at least one listed app, in API order. */
  get availableCategories(): AppCategoryOption[] {
    const cache = this.availableCategoriesCache;
    if (cache && cache.apps === this.allApps && cache.categories === this.categories) {
      return cache.options;
    }
    const options = this.groupApps(this.listedApps).map(({ key, label }) => ({ key, label }));
    this.availableCategoriesCache = { apps: this.allApps, categories: this.categories, options };
    return options;
  }

  /** The listed apps narrowed by search text, selected categories and connection status. */
  get filteredApps(): App[] {
    const term = this.searchTerm.trim().toLowerCase();
    return this.listedApps.filter((app) => {
      if (this.hasCategoryFilter && !this.selectedCategories.includes(app.category)) {
        return false;
      }
      if (this.connectedOnly && !app.isInstalled) {
        return false;
      }
      if (!term) {
        return true;
      }
      const haystack = [app.name, app.description, this.getCategoryLabel(app.category)]
        .filter((value) => !!value)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }

  /** Filtered apps grouped by category in API order; categories the API did not list trail at the end. */
  get appGroups(): AppCategoryGroup[] {
    return this.groupApps(this.filteredApps);
  }

  private groupApps(apps: App[]): AppCategoryGroup[] {
    const groups: AppCategoryGroup[] = this.categories.map((category) => ({ ...category, apps: [] }));
    apps.forEach((app) => {
      let group = groups.find((candidate) => candidate.key === app.category);
      if (!group) {
        group = { key: app.category, label: this.humanizeCategoryKey(app.category), apps: [] };
        groups.push(group);
      }
      group.apps.push(app);
    });
    return groups.filter((group) => group.apps.length > 0);
  }

  private humanizeCategoryKey(key: string): string {
    return (key || '')
      .split(/[_\s-]+/)
      .filter((word) => !!word)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private persistFilters(): void {
    const state: AppsFilterState = {
      searchTerm: this.searchTerm,
      selectedCategories: this.selectedCategories,
      connectedOnly: this.connectedOnly,
    };
    this.filterStorage.set(this.FILTER_STORAGE_KEY, state);
  }

  private forgetBusinessStateIfSwitched(): void {
    if (!this.loadedForBusinessId || this.loadedForBusinessId === this.user.activeBusiness?.id) {
      return;
    }
    this.forgetBusinessState();
  }

  private forgetBusinessState(): void {
    this.allApps = [];
    this.categories = [];
    this.loadedForBusinessId = null;
    this.searchTerm = '';
    this.selectedCategories = [];
    this.connectedOnly = false;
    this.filterStorage.remove(this.FILTER_STORAGE_KEY);
  }

  private restoreFilters(): void {
    const saved: Partial<AppsFilterState> = this.filterStorage.get(this.FILTER_STORAGE_KEY);
    if (!saved) {
      return;
    }
    this.searchTerm = typeof saved.searchTerm === 'string' ? saved.searchTerm : '';
    this.selectedCategories = Array.isArray(saved.selectedCategories)
      ? saved.selectedCategories.filter((key) => typeof key === 'string')
      : [];
    this.connectedOnly = saved.connectedOnly === true;
  }

  get scheduleAppointmentsApp(): App {
    return this.allApps.find(app => app.isInstalled);
  }

  getAvailableApps(): Observable<App[]> {
    const businessId = this.user.activeBusiness?.id;
    return this.http.get(`${getApiUrl()}apps`, { headers: { 'X-Business-ID': businessId } })
      .pipe(map((res: any) => {
        this.categories = this.categoriesFactory(res?.meta?.categories);
        this.allApps = this.appsFactory(res?.data);
        this.loadedForBusinessId = businessId;
        return this.allApps;
      })) as Observable<App[]>;
  }

  install(app: App, data: any = {}): Observable<any> {
    this.analytics.trackEvent(AnalyticsEvent.AppInstalled, { app: app.key });
    return this.http.post(`${getApiUrl()}apps/${app.id}/install`, this.prepareFormData(app, data), { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((res: any) => res.data));
  }

  uninstall(app: App): Observable<any> {
    return this.http.post(`${getApiUrl()}apps/${app.id}/uninstall`, {}, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } });
  }

  verify(appId: string): Observable<AppStatus> {
    return this.http.get(`${getApiUrl()}apps/${appId}/check-install`, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((res: any) => {
        const app = res.data;
        return app?.installStatus as AppStatus;
      }));
  }

  discoverFormOptions(app: App, data: Record<string, any>): Observable<Record<string, AppFormFieldOption[]>> {
    return this.http.post(`${getApiUrl()}apps/${app.id}/form-options`, data, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((res: any) => (res.data?.options || []).reduce((options, entry) => {
        options[entry.field] = entry.options || [];
        return options;
      }, {} as Record<string, AppFormFieldOption[]>)));
  }

  synchronize(app: App): Observable<any> {
    return this.http.post(`${getApiUrl()}apps/${app.id}/synchronize`, {}, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((res: any) => {
        const app = res.data;
        return app?.installStatus as AppStatus;
      }));
  }

  updateCrmFilters(app: App, filters: CrmFilters): Observable<CrmFilters> {
    return this.http.put(`${getApiUrl()}apps/${app.id}/crm-filters`, filters, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((res: any) => res?.data as CrmFilters));
  }

  getUrl(app: App, isOauthFlow = true): Observable<AppSetup> {
    const data = {};
    if (isOauthFlow) {
      try {
        data['callbackUrl'] = `${window.location.origin}/admin/finalize/${app.key}`;
      } catch (error) {
        data['callbackUrl'] = `/finalize/${app.key}`;
      }
      app.setupData.formFields.forEach((field: AppFormField) => data[`${field.name}`] = app.setupData.form.controls[`${field.name}`]?.value);
    }
    return this.http.post(`${getApiUrl()}apps/${app.id}/install`, data, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((res: any) => {
        const setup = res.data;
        return { url: setup.redirectUrl };
      }));
  }

  finalizeIntegration(params: any, appId: string) {
    return this.http.post(`${getApiUrl()}apps/${appId}/finalize`, params, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(
        map((res: any) => res?.data));
  }

  getAppData(app: App): Observable<AppProfile> {
    return this.http.get(`${getApiUrl()}apps/${app.id}`, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } }).pipe(map((res: any) => this.buildAppProfile(res, app)));
  }

  refreshApp(app: App) {
    this.getAppData(app).subscribe((appData: AppProfile) => {
      app.details.profile = appData;
      app.inProgress = false;
      if (app.hasFormFields) {
        this.resetForm(app);
      }
    }, () => app.inProgress = false);
  }

  addFormArrayField(formArray: UntypedFormArray, fieldName: string, isRequired: boolean) {
    const fieldControl = this.formBuilder.control({ value: null, disabled: false });
    fieldControl['fieldName'] = fieldName;
    formArray?.push(fieldControl);
    if (isRequired) {
      setRequired(fieldControl);
    }
  }

  removeFormArrayField(formArray: UntypedFormArray, index: number) {
    formArray?.removeAt(index);
  }

  resetForm(app: App) {
    if (app.hasFormFlow) {
      if (app.setupData?.formFields?.some(field => !!field.fields)) {
        // if app has an array form, we need to rebuild the form
        app.setupData.form = this.buildFormGroup(app, app.setupData.formFields);
      } else {
        app.setupData?.form?.reset();
      }
    }
  }

  getCommerceSettings(businessId: string): Observable<any> {
    return this.http.get(`${getApiUrl()}commerce-settings`, { headers: { 'X-Business-ID': businessId } })
      .pipe(map((res: any) => res?.data));
  }

  updateCommerceSettings(businessId: string, data: any): Observable<any> {
    return this.http.put(`${getApiUrl()}commerce-settings`, data, { headers: { 'X-Business-ID': businessId } })
      .pipe(map((res: any) => res?.data));
  }

  getCommerceDiagnostics(businessId: string): Observable<any> {
    return this.http.get(`${getApiUrl()}commerce-settings/diagnostics`, { headers: { 'X-Business-ID': businessId } })
      .pipe(map((res: any) => res?.data));
  }

  updateAppProfile(app: App, data: any = {}): Observable<AppProfile> {
    return this.http.patch(`${getApiUrl()}apps/${app.id}/profile`, this.prepareFormData(app, data))
      .pipe(map((res: any) => this.buildAppProfile(res, app)));
  }

  buildTranslation(translationKey: string, translationConfig: AppTranslationConfig) {
    const appType = this.translate.instant(`APP.${translationConfig.app?.type?.toUpperCase()}`).toLowerCase();
    const appKey = translationConfig.useAppKey && translationConfig.app ? `${translationConfig.app.key?.toUpperCase()}.` : '';
    const status = translationConfig.useStatus ? `STATUS.${translationConfig.app?.status?.toUpperCase()}.` : '';
    const finalTranslateKey = `APP.${appKey}${status}${translationKey}`;
    const translationWithAppKey = this.translate.instant(finalTranslateKey, { value: translationConfig.app?.name, appType });
    return translationWithAppKey === finalTranslateKey ? this.translate.instant(`APP.${status}${translationKey}`, { value: translationConfig.app?.name, appType }) : translationWithAppKey;
  }

  getHelpTextPerField(fieldName: string): string {
    let translationKey = fieldName.startsWith('APP.') ? fieldName.split('.').pop() : fieldName;
    translationKey = `APP.FIELDS_DESCRIPTION.${translationKey === fieldName ? convertCamelCaseToSnakeCase(translationKey, CaseType.UpperCase) : translationKey}`;
    const translation = this.translate.instant(translationKey);
    return translation === translationKey ? '' : translation;
  }

  getFieldTranslationKey(app: App, fieldName: string): string {
    const normalizedFieldName = fieldName.startsWith('APP.')
      ? fieldName.split('.').pop()
      : convertCamelCaseToSnakeCase(fieldName, CaseType.UpperCase);
    const appSpecificKey = `APP.${app.key?.toUpperCase()}.FIELDS.${normalizedFieldName}`;
    const appSpecificTranslation = this.translate.instant(appSpecificKey);

    if (appSpecificTranslation !== appSpecificKey) {
      return appSpecificKey;
    }

    const genericKey = `APP.FIELDS.${normalizedFieldName}`;
    return genericKey;
  }

  private buildAppProfile(res: any, app: App): AppProfile {
    const appData: RawApp = res.data;
    app.buildFormFields(appData.formFields);
    // Update the app's status from the API response
    app.status = appData.installStatus || null;
    // Update the app's config if it exists in the response
    if (appData.config) {
      app.config = appData.config;
    }
    const formFieldMapper = (field: RawFormField | AppFormField) => {
      let value = field.type === AppFieldType.Boolean ? (field.value ? 'ON' : 'OFF') : (field.value || '');
      if (field.type === AppFieldType.Select && field.options) {
        value = field.options.find(option => String(option.value) === String(field.value))?.label || '';
      }
      if (field.type === AppFieldType.Array && Array.isArray(field.value)) {
        value = (value as unknown as any[]).map(v => v[field.fields[0].name]).join(', ');
      }
      return {
        name: `APP.${app.key.toUpperCase()}.FIELDS.${convertCamelCaseToSnakeCase(field.name, CaseType.UpperCase)}`,
        value,
        behaviour: field.behaviour || FieldBehaviour.Plain,
        editability: field.editability || AppFieldStage.Never,
      };
    };
    const appProfile = { fields: appData.formFields.filter(field => field.behaviour !== FieldBehaviour.Hidden).map(formFieldMapper) } as AppProfile;
    return appProfile;
  }

  private createForm(app: App, formFieldProperty = 'formFields', formProperty = 'form') {
    const appFormFields = app.setupData?.[`${formFieldProperty}`];
    if (appFormFields?.length > 0) {
      app.setupData[`${formFieldProperty}`] = sortArrayByNumericAttribute(appFormFields, 'position');
      app.setupData[`${formProperty}`] = this.buildFormGroup(app, appFormFields);
      this.setRequiredFields(appFormFields, app.setupData[`${formProperty}`]);
    }
  }

  private buildFormGroup(app: App, formFields: AppFormField[]): UntypedFormGroup {
    const formFieldsObject = {};
    formFields.forEach((field) => {
      if (field.editability !== AppFieldStage.Never) {
        if (field.fields?.length > 0) {
          formFieldsObject[`${field.name}`] = this.formBuilder.array([]);
          const arrayField = field.fields[0];
          if (field.value && Array.isArray(field.value)) {
            field.value.forEach(() => {
              this.addFormArrayField(formFieldsObject[`${field.name}`], arrayField.name, arrayField.isRequired);
            });
          } else {
            this.addFormArrayField(formFieldsObject[`${field.name}`], arrayField.name, arrayField.isRequired);
          }
        } else {
          formFieldsObject[`${field.name}`] = this.addFormControlForField(field, app);
        }
      }
    });
    return this.formBuilder.group(formFieldsObject);
  }

  private setRequiredFields(formFields: AppFormField[], form: UntypedFormGroup) {
    const requiredControlKeys = formFields.filter((field) => field.isRequired).map((field) => field.name);
    requiredControlKeys.forEach((controlKey: string) => setRequired(form.get(controlKey)));
    const requiredTrueControlKeys = formFields.filter((field) => field.isRequired && field.type === AppFieldType.Boolean && field.validation === AppFormFieldValidation.RequiredBoolean).map((field) => field.name);
    requiredTrueControlKeys.forEach((controlKey: string) => {
      form.get(controlKey).setValidators([Validators.requiredTrue]);
    });
  }

  private prepareFormData(app: App, rawFormValue: any): any {
    const formData: FormData | {} = {};

    Object.keys(rawFormValue).forEach(field => {
      const fieldValue = rawFormValue[field];
      if (fieldValue && Array.isArray(fieldValue)) {
        const fieldName = app?.setupData?.formFields?.find(f => f.name === field)?.fields?.[0].name;
        formData[field] = fieldValue.map(value => {
          return { [`${fieldName}`]: value };
        });
      } else {
        formData[field] = fieldValue;
      }
    });
    return this.buildFormData(app, formData);
  }

  private buildFormData(app: App, data: any): FormData {
    if (app.hasAtLeastOneFileAppFieldType) {
      const formData = new FormData();
      const headers = new HttpHeaders();
      headers.append('Content-Type', 'multipart/form-data');
      for (const [key, value] of Object.entries(data)) {
        if (value) {
          formData.append(convertCamelCaseToSnakeCase(key, CaseType.LowerCase), value as string);
        }
      }
      return formData;
    } else {
      return data;
    }
  }

  private categoriesFactory(rawCategories: unknown): AppCategoryOption[] {
    if (!Array.isArray(rawCategories)) {
      return [];
    }
    return rawCategories
      .filter((category): category is Partial<AppCategoryOption> => !!category && typeof category.key === 'string')
      .map((category) => ({ key: category.key, label: category.label || this.humanizeCategoryKey(category.key) }));
  }

  private appsFactory(rawApps: RawApp[]): App[] {
    const allApps: App[] = rawApps?.map((rawApp: RawApp) => {
      const app = new DefaultApp(rawApp.referenceId as AppKey, rawApp.name, rawApp.id) as App;
      app.buildApp(rawApp);
      this.createForm(app);
      return app;
    });
    allApps.forEach((app: App) => app.setStatus(allApps));
    return allApps;
  }

  private addFormControlForField(field: AppFormField, app: App): UntypedFormControl {
    const fieldControl: UntypedFormControl = this.formBuilder.control({ value: app.getFieldValue(field), disabled: false });
    fieldControl['fieldName'] = field.name;
    if (field.isRequired) {
      setRequired(fieldControl);
    }
    return fieldControl;
  }

}
